import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PageHeader } from '@/components/PageHeader';
import { BreadcrumbItem } from '@/components/Breadcrumb';
import SignaturePad from '@/components/ui/SignaturePad';
import { Shield, Edit, Download, X } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas-pro';
import { message } from 'antd';
import Spinner from '@/components/Spinner';
import {
  Header13WAppendix,
  Content13WAppendix,
  Contract13WData,
  AppendixValues
} from '@/components/Contracts';
import { CompanyInfo, InvestorInfo } from '@/components/Contracts/types';

// Default company info
const defaultCompanyInfo: CompanyInfo = {
  name: 'CÔNG TY CỔ PHẦN BEQ HOLDINGS',
  address: 'Số 67 Đinh Thị Thi, P. Hiệp Bình, TP.HCM, Việt Nam',
  taxCode: '0317746326',
  bankAccount: '1036616688',
  bank: 'Ngân hàng TMCP Ngoại thương Việt Nam (Vietcombank) - Chi nhánh: Thủ Đức',
  representative: 'Bà Tạ Thị Mỹ Phương',
  position: 'Tổng Giám đốc',
  representativeId: '079182006638',
  representativeIssueDate: '21/02/2021'
};

// Initial investor info
const defaultInvestorInfo: InvestorInfo = {
  fullName: 'Nguyễn Văn An',
  idNumber: '001234567890',
  dob: '15/03/1985',
  issueDate: '21/02/2021',
  issuePlace: 'Cục Cảnh sát ĐKQL cư trú và DLQG về dân cư',
  address: '123 Đường Trần Duy Hưng, Cầu Giấy, Hà Nội',
  email: 'an.nguyen@email.com',
  phone: '0901234567',
  bankAccount: '19036698686868',
  bank: 'Ngân hàng TMCP Ngoại thương Việt Nam (Vietcombank)'
};

// Initial contract data for 13 Weeks Challenge
const initialContractData: Contract13WData = {
  contractNumber: 'LUMIR/BEQ-001',
  signDate: new Date().toLocaleDateString('vi-VN'),
  investor: defaultInvestorInfo,
  company: defaultCompanyInfo
};

// Appendix values
const appendixValues: AppendixValues = {
  preTaxValue: '11.818.182',
  vatAmount: '1.181.818',
  totalValue: '13.000.000'
};

const WeeksChallenge13: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [contractData, setContractData] = useState<Contract13WData>(initialContractData);
  const [signature, setSignature] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const contractRef = useRef<HTMLDivElement>(null);

  const handleSignatureChange = (sig: string | null) => {
    setSignature(sig);
    setShowPreview(!!sig);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setContractData(prev => ({
      ...prev,
      investor: {
        ...prev.investor,
        [name]: value
      }
    }));
  };

  const exportToPDF = async () => {
    if (!contractRef.current) return;
    setIsExporting(true);

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const element = contractRef.current;
      const blocks = element.querySelectorAll<HTMLElement>('.pdf-block');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pdfWidth - (margin * 2);

      let currentY = margin;

      for (let i = 0; i < blocks.length; i++) {
        const block = blocks[i];
        const isLogo = block.classList.contains('pdf-logo');

        const canvas = await html2canvas(block, {
          scale: isLogo ? 1 : 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false
        });

        const quality = isLogo ? 0.5 : 0.85;
        const imgData = canvas.toDataURL('image/jpeg', quality);
        const imgProps = pdf.getImageProperties(imgData);
        const blockHeightInPdf = (imgProps.height * contentWidth) / imgProps.width;

        if (currentY + blockHeightInPdf > pdfHeight - margin) {
          pdf.addPage();
          currentY = margin;
        }

        pdf.addImage(imgData, 'JPEG', margin, currentY, contentWidth, blockHeightInPdf, undefined, isLogo ? 'SLOW' : 'FAST');
        currentY += blockHeightInPdf + (isLogo ? 2 : 5);
      }

      const fileName = `HopDong_13W_${contractData.contractNumber}.pdf`;
      const pdfBlob = pdf.output('blob');
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

      pdf.save(fileName);
      return file;

    } catch (error) {
      console.error('Error:', error);
      alert('Lỗi xuất PDF. Vui lòng thử lại.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSubmit = () => {
    if (!signature) {
      alert('Vui lòng vẽ chữ ký của bạn');
      return;
    }

    setIsSubmitting(true);

    exportToPDF().then(file => {
      if (file) {
        message.success('Ký hợp đồng thành công!');
        navigate(`/plans/${id}/checkout`);
      } else {
        message.error('Không thể tạo file hợp đồng. Vui lòng thử lại.');
      }
      setIsSubmitting(false);
    });
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Dashboard', path: '/home' },
    { label: 'Gói đầu tư', path: '/plans' },
    { label: '13 Weeks Challenge' },
  ];

  return (
    <div className="dark:bg-dark-bg">
      <Helmet>
        <title>Hợp đồng 13 Weeks Challenge - BeQs Reward</title>
      </Helmet>

      <PageHeader title="Ký hợp đồng 13W Challenge" breadcrumbs={breadcrumbs} />

      <div className="!mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 !gap-6">
          {/* Contract Display */}
          <div className="lg:col-span-2">
            <div className="!bg-white dark:!bg-dark-card !rounded-xl !shadow-sm !p-8 !relative" ref={contractRef}>
              {/* Header Component */}
              <Header13WAppendix
                appendixNumber={`PL-${contractData.contractNumber}`}
                mainContractNumber={contractData.contractNumber}
                mainContractDate={contractData.signDate}
              />

              {/* Party Info */}
              <div className="!mb-6 pdf-block">
                <p className="!text-sm !text-gray-700 dark:!text-gray-300 !mb-4">
                  Hôm nay, ngày {contractData.signDate} tại Công ty Cổ phần BeQ Holdings, chúng tôi gồm có:
                </p>

                <div className="!space-y-4">
                  {/* Bên A */}
                  <div>
                    <h4 className="!font-semibold !text-gray-900 dark:!text-white !mb-2">
                      Bên A: {contractData.company.name}
                    </h4>
                    <div className="!text-sm !text-gray-900 dark:!text-gray-400 !space-y-1">
                      <p>Địa chỉ: {contractData.company.address}</p>
                      <p>MST: {contractData.company.taxCode}</p>
                      <p>Số tài khoản: {contractData.company.bankAccount} - {contractData.company.bank}</p>
                      <p>Đại diện: {contractData.company.representative} – {contractData.company.position}</p>
                      <p>(CCCD: {contractData.company.representativeId}, cấp ngày {contractData.company.representativeIssueDate})</p>
                    </div>
                  </div>

                  {/* Bên B */}
                  <div>
                    <h4 className="!font-semibold !text-gray-900 dark:!text-white !mb-2">
                      Bên B: {contractData.investor.fullName}
                    </h4>
                    <div className="!text-sm !text-gray-900 dark:!text-gray-400 !space-y-1">
                      <p>CCCD: {contractData.investor.idNumber} &nbsp;&nbsp;&nbsp; Ngày cấp: {contractData.investor.issueDate}</p>
                      <p>Địa chỉ: {contractData.investor.address}</p>
                      <p>Email: {contractData.investor.email} &nbsp;&nbsp;&nbsp; SĐT: {contractData.investor.phone}</p>
                      <p>Số tài khoản: {contractData.investor.bankAccount} - {contractData.investor.bank}</p>
                    </div>
                  </div>

                  <p className="!text-sm !text-gray-900 dark:!text-gray-400 italic">
                    (Cùng gọi riêng là "Bên", gọi chung là "Các Bên")
                  </p>
                </div>
              </div>

              {/* Content Component */}
              <Content13WAppendix
                mainContractNumber={contractData.contractNumber}
                mainContractDate={contractData.signDate}
                values={appendixValues}
              />

              {/* Signature */}
              <div className="grid grid-cols-2 !gap-8 !mt-8 !pt-6 !border-t pdf-block">
                <div className="text-center">
                  <p className="!text-sm !font-semibold !text-gray-900 dark:!text-white !mb-2">ĐẠI DIỆN BÊN A</p>
                  <p className="!text-xs !text-gray-500 dark:!text-gray-400 !mb-4">(Ký tên, đóng dấu)</p>
                  <div className="!h-20 flex items-center justify-center !border-b !border-gray-300 dark:!border-gray-900">
                    <p className="!text-sm !text-gray-400 italic">Đã ký điện tử</p>
                  </div>
                  <p className="!text-sm !font-medium !text-gray-900 dark:!text-white !mt-2">
                    {contractData.company.representative.replace('Bà ', '').toUpperCase()}
                  </p>
                </div>

                <div className="text-center">
                  <p className="!text-sm !font-semibold !text-gray-900 dark:!text-white !mb-2">BÊN B</p>
                  <p className="!text-xs !text-gray-500 dark:!text-gray-400 !mb-4">(Ký, ghi rõ họ tên)</p>
                  <div className="!h-20 flex items-center justify-center">
                    {signature ? (
                      <img src={signature} alt="Signature" className="max-h-full" />
                    ) : (
                      <p className="!text-sm !text-gray-400 italic">Chưa ký</p>
                    )}
                  </div>
                  <p className="!text-sm !font-medium !text-gray-900 dark:!text-white !mt-2">
                    {contractData.investor.fullName.toUpperCase()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar: Signature Panel */}
          <div className="lg:col-span-1">
            <div className="!bg-white dark:!bg-dark-card !rounded-xl !shadow-sm !p-6 !sticky !top-24">
              <div className="flex items-center !gap-3 !mb-2">
                <div>
                  <h3 className="!text-lg !font-semibold !text-gray-900 dark:!text-white !mb-0">
                    Ký hợp đồng
                  </h3>
                  <p className="!text-sm !text-gray-700 dark:!text-gray-200">
                    Xác nhận thông tin và ký điện tử
                  </p>
                </div>
              </div>

              <div className="!space-y-4">
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="!w-full flex items-center justify-center !gap-2 !px-4 !py-2.5 !text-sm !font-medium !text-gray-700 dark:!text-gray-300 !bg-white dark:!bg-gray-800 !border !border-gray-300 dark:!border-gray-900 !rounded-lg hover:!bg-gray-50 dark:hover:!bg-gray-700 !transition-colors"
                >
                  <Edit className="!w-4 !h-4" />
                  Chỉnh sửa thông tin cá nhân
                </button>

                <div>
                  <label className="block !text-sm !font-medium !text-gray-700 dark:!text-gray-300 !mb-2">
                    Chữ ký điện tử <span className="!text-red-500">*</span>
                  </label>
                  <div className='flex !justify-center'>
                    <SignaturePad onSignatureChange={handleSignatureChange} width={400} height={150} />
                  </div>
                  {!signature && (
                    <p className="!text-xs !text-red-500 !mt-1">Vui lòng vẽ chữ ký để tiếp tục</p>
                  )}
                </div>

                {showPreview && signature && (
                  <div className="!bg-gray-50 dark:!bg-gray-800 !rounded-md !p-4">
                    <p className="!text-sm !font-medium !text-gray-700 dark:!text-gray-300 !mb-2">
                      Xem trước chữ ký:
                    </p>
                    <div className="!bg-white dark:!bg-gray-900 !border-2 !border-gray-200 dark:!border-gray-700 !rounded-md !p-3 flex items-center justify-center">
                      <img src={signature} alt="Signature preview" className="!max-h-20" />
                    </div>
                  </div>
                )}

                <button
                  onClick={exportToPDF}
                  disabled={isExporting}
                  className="!w-full flex items-center justify-center !gap-2 !px-4 !py-2.5 !text-sm !font-medium !text-gray-700 dark:!text-gray-300 !bg-white dark:!bg-gray-800 !border !border-gray-300 dark:!border-gray-900 !rounded-lg hover:!bg-gray-50 dark:hover:!bg-gray-700 disabled:!opacity-50 !transition-colors"
                >
                  <Download className="!w-4 !h-4" />
                  {isExporting ? 'Đang xuất PDF...' : 'Tải hợp đồng PDF'}
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={!signature || isExporting}
                  className="!w-full !px-6 !py-3 !bg-blue-600 !text-white !rounded-md !font-semibold hover:!bg-blue-700 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors"
                >
                  Ký hợp đồng ngay
                </button>

                <div className="!bg-blue-50 dark:!bg-blue-900/20 !border !border-blue-200 dark:!border-blue-800 !rounded-md !p-3 !space-y-1">
                  <p className="!text-xs !text-blue-800 dark:!text-blue-300 flex items-center !gap-2">
                    <Shield className="!w-3 !h-3" />
                    Chữ ký điện tử có giá trị pháp lý
                  </p>
                  <p className="!text-xs !text-blue-800 dark:!text-blue-300 flex items-center !gap-2">
                    <Shield className="!w-3 !h-3" />
                    Hợp đồng được lưu trữ an toàn
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-dark-card rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Chỉnh sửa thông tin cá nhân</h3>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Họ và tên</label>
                  <input
                    type="text"
                    name="fullName"
                    value={contractData.investor.fullName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-900 dark:text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Số CCCD/CMND</label>
                  <input
                    type="text"
                    name="idNumber"
                    value={contractData.investor.idNumber}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-900 dark:text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Ngày sinh</label>
                  <input
                    type="text"
                    name="dob"
                    value={contractData.investor.dob}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-900 dark:text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Ngày cấp</label>
                  <input
                    type="text"
                    name="issueDate"
                    value={contractData.investor.issueDate}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-900 dark:text-white"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nơi cấp</label>
                  <input
                    type="text"
                    name="issuePlace"
                    value={contractData.investor.issuePlace}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-900 dark:text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Số điện thoại</label>
                  <input
                    type="text"
                    name="phone"
                    value={contractData.investor.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-900 dark:text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={contractData.investor.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-900 dark:text-white"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Địa chỉ</label>
                  <input
                    type="text"
                    name="address"
                    value={contractData.investor.address}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-900 dark:text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Số tài khoản ngân hàng</label>
                  <input
                    type="text"
                    name="bankAccount"
                    value={contractData.investor.bankAccount}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-900 dark:text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Ngân hàng</label>
                  <input
                    type="text"
                    name="bank"
                    value={contractData.investor.bank}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>
            <div className="p-6 border-t dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-900 dark:hover:bg-gray-700"
              >
                Đóng
              </button>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Xác nhận
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {(isExporting || isSubmitting) && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-dark-card p-6 rounded-xl shadow-xl flex flex-col items-center gap-4">
            <Spinner size={40} />
            <p className="text-gray-900 dark:text-white font-medium">
              {isExporting ? 'Đang tạo hợp đồng...' : 'Đang xử lý...'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeksChallenge13;
